import React from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';

// Context
import { AuthProvider } from '../../contexts/AuthContext';
import { ThemeProvider } from '../../contexts/ThemeContext';
import { NavigationProvider } from '../../contexts/NavigationContext';

// Layouts
import Header from '../Layouts/Header';
import AccordionNavigation from '../Navigation/AccordionNavigation/AccordionNavigation';
import PageContent from '../Layouts/PageContent';
import Footer from '../Layouts/Footer';

// Pages
import SignInPage from '../Pages/Pages/SignInPage';
import SignUpPage from '../Pages/Pages/SignUpPage';
import PasswordResetRequestPage from '../Pages/Pages/PasswordResetRequestPage';
import PasswordResetNewPasswordPage from '../Pages/Pages/PasswordResetNewPasswordPage';
import DashboardPage from '../Pages/Pages/DashboardPage';
import ProspectsIndexPage from '../Pages/Pages/ProspectsIndexPage';
import ProspectProfilePage from '../Pages/Pages/ProspectProfilePage';
import DraftTrackerPage from '../Pages/Pages/DraftTrackerPage';
import PlayersIndexPage from '../Pages/Pages/PlayersIndexPage';
import PlayerProfilePage from '../Pages/Pages/PlayerProfilePage';
import InjuryReportPage from '../Pages/Pages/InjuryReportPage';
import TeamsIndexPage from '../Pages/Pages/TeamsIndexPage';
import TeamProfilePage from '../Pages/Pages/TeamProfilePage';
import ProjectionsIndexPage from '../Pages/Pages/ProjectionsIndexPage';
import ProjectionTeamPage from '../Pages/Pages/ProjectionTeamPage';
import PlayerRankingsPage from '../Pages/Pages/PlayerRankingsPage';
import EliminatorPlayerMatchupsPage from '../Pages/Pages/EliminatorPlayerMatchupsPage';
import RostersIndexPage from '../Pages/Pages/RostersIndexPage';
import RosterProfilePage from '../Pages/Pages/RosterProfilePage';
import CoursesIndexPage from '../Pages/Pages/CoursesIndexPage';
import CourseProfilePage from '../Pages/Pages/CourseProfilePage';
import MarketingPage from './Pages/MarketingPage';
import PricingPage from './Pages/PricingPage';
import UserProfilePage from './Pages/UserProfilePage';
import HelpCenterIndexPage from './Pages/HelpCenterIndexPage';
import ArticlePage from './Pages/ArticlePage';
import NewsCenterIndexPage from './Pages/NewsCenterIndexPage';
import GlossaryIndexPage from './Pages/GlossaryIndexPage';
import FrontOfficePage from './Pages/FrontOfficePage';
import BestBallZonePage from './Pages/BestBallZonePage';
import AlertsInboxPage from './Pages/AlertsInboxPage';
import YahooCallbackPage from './Pages/YahooCallbackPage';
import GradeMyDraftPage from './Pages/GradeMyDraftPage';
import DraftBuildSpectrumPage from './Pages/DraftBuildSpectrumPage';
import SpikeVolatilityIndexPage from './Pages/SpikeVolatilityIndexPage';
import ChampionshipWeeksPage from './Pages/ChampionshipWeeksPage';
import FitnessHubPage from './Pages/FitnessHubPage';
import AiTrainerPage from './Pages/AiTrainerPage';
import NutritionLabPage from './Pages/NutritionLabPage';
import ClassroomPage from './Pages/ClassroomPage';
import WhiteboardPage from './Pages/WhiteboardPage';
import FitnessProfilesIndexPage from './Pages/FitnessProfilesIndexPage';
import FitnessProfileDetailPage from './Pages/FitnessProfileDetailPage';
import FitnessProfileCreatePage from './Pages/FitnessProfileCreatePage';
import FitnessProfileCompleteIntakePage from './Pages/FitnessProfileCompleteIntakePage';
import FriendsPage from './Pages/FriendsPage';
import LinkedHouseholdsPage from './Pages/LinkedHouseholdsPage';
import ChallengeWirePage from './Pages/ChallengeWirePage';
import CommunityChallengeShowPage from './Pages/CommunityChallengeShowPage';
import ChallengeInvitePage from './Pages/ChallengeInvitePage';
import HouseholdLoginSetupRedirectPage from './Pages/HouseholdLoginSetupRedirectPage';
import DynamicFFLogoRoute from '../DynamicFFLogoRoute/DynamicFFLogoRoute';

// PrivateRoute
import PrivateRoute from '../PrivateRoute/PrivateRoute';
import AuthenticatedWelcomeModal from '../User/AuthenticatedWelcomeModal';
import AuthenticatedPricingModal from '../User/AuthenticatedPricingModal';
import AuthenticatedSubscriptionSuccessModal from '../User/AuthenticatedSubscriptionSuccessModal';

export default function App({
	endpoints,
	stripe_public_key,
	stripe_product_essential_id,
	stripe_product_advanced_id,
	stripe_product_ultimate_id
}) {
	return (
		<Router>
			<AuthProvider endpoints={endpoints}>
				<ThemeProvider
					updateThemeEndpoint={endpoints.update_user_theme_preference_endpoint}
					updateFitnessProfileEndpoint={endpoints.update_user_fitness_profile_endpoint}
				>
					<NavigationProvider>
						<AuthenticatedWelcomeModal
							create_checkout_session_endpoint={
								endpoints.create_checkout_session_endpoint
							}
							stripe_public_key={stripe_public_key}
							stripe_product_essential_id={stripe_product_essential_id}
							stripe_product_advanced_id={stripe_product_advanced_id}
							stripe_product_ultimate_id={stripe_product_ultimate_id}
						/>
						<AuthenticatedPricingModal
							create_checkout_session_endpoint={
								endpoints.create_checkout_session_endpoint
							}
							stripe_public_key={stripe_public_key}
							stripe_product_essential_id={stripe_product_essential_id}
							stripe_product_advanced_id={stripe_product_advanced_id}
							stripe_product_ultimate_id={stripe_product_ultimate_id}
						/>
						<AuthenticatedSubscriptionSuccessModal />
						<Header
							search_endpoint={endpoints.search_endpoint}
							sign_out_endpoint={endpoints.sign_out_endpoint}
						/>
						<AccordionNavigation />
						<PageContent>
						<Routes>
							<Route
								path="/signin"
								element={
									<SignInPage sign_in_endpoint={endpoints.sign_in_endpoint} />
								}
							/>
							<Route
								path="/signup"
								element={
									<SignUpPage
										create_user_endpoint={endpoints.create_user_endpoint}
									/>
								}
							/>
							<Route
								path="/password-reset"
								element={
									<PasswordResetRequestPage
										user_password_create_endpoint={
											endpoints.user_password_create_endpoint
										}
									/>
								}
							/>
							<Route
								path="/password-reset/update"
								element={
									<PasswordResetNewPasswordPage
										user_password_update_endpoint={
											endpoints.user_password_update_endpoint
										}
									/>
								}
							/>
							<Route
								path="/household-setup/:token"
								element={<HouseholdLoginSetupRedirectPage />}
							/>
							<Route
								path="/challenge-invite/:token/accept"
								element={
									<ChallengeInvitePage
										action="accept"
										getChallengeInviteEndpoint={endpoints.get_challenge_invite_endpoint}
										acceptChallengeInviteEndpoint={endpoints.accept_challenge_invite_endpoint}
										declineChallengeInviteEndpoint={endpoints.decline_challenge_invite_endpoint}
									/>
								}
							/>
							<Route
								path="/challenge-invite/:token/decline"
								element={
									<ChallengeInvitePage
										action="decline"
										getChallengeInviteEndpoint={endpoints.get_challenge_invite_endpoint}
										acceptChallengeInviteEndpoint={endpoints.accept_challenge_invite_endpoint}
										declineChallengeInviteEndpoint={endpoints.decline_challenge_invite_endpoint}
									/>
								}
							/>
							<Route
								path="/challenge-invite/:token"
								element={
									<ChallengeInvitePage
										getChallengeInviteEndpoint={endpoints.get_challenge_invite_endpoint}
										acceptChallengeInviteEndpoint={endpoints.accept_challenge_invite_endpoint}
										declineChallengeInviteEndpoint={endpoints.decline_challenge_invite_endpoint}
									/>
								}
							/>
							<Route
								path="/pricing"
								element={
									<PricingPage
										create_checkout_session_endpoint={
											endpoints.create_checkout_session_endpoint
										}
										stripe_public_key={stripe_public_key}
										stripe_product_essential_id={stripe_product_essential_id}
										stripe_product_advanced_id={stripe_product_advanced_id}
										stripe_product_ultimate_id={stripe_product_ultimate_id}
									/>
								}
							/>
							<Route
								path="/"
								element={
									<DynamicFFLogoRoute>
										<MarketingPage
											create_checkout_session_endpoint={
												endpoints.create_checkout_session_endpoint
											}
											stripe_public_key={stripe_public_key}
											stripe_product_essential_id={stripe_product_essential_id}
											stripe_product_advanced_id={stripe_product_advanced_id}
											stripe_product_ultimate_id={stripe_product_ultimate_id}
										/>
									</DynamicFFLogoRoute>
								}
							/>
							<Route
								path="/injury-report"
								element={
									<PrivateRoute>
										<InjuryReportPage />
									</PrivateRoute>
								}
							/>
							<Route
								path="/players"
								element={
									<PlayersIndexPage
										get_player_endpoint={endpoints.get_player_endpoint}
										get_players_endpoint={endpoints.get_players_endpoint}
										get_team_endpoint={endpoints.get_team_endpoint}
										get_roster_by_user_endpoint={
											endpoints.get_roster_by_user_endpoint
										}
										create_roster_by_user_endpoint={
											endpoints.create_roster_by_user_endpoint
										}
										update_roster_by_user_endpoint={
											endpoints.update_roster_by_user_endpoint
										}
										create_roster_player_by_roster_endpoint={
											endpoints.create_roster_player_by_roster_endpoint
										}
										delete_roster_player_by_roster_endpoint={
											endpoints.delete_roster_player_by_roster_endpoint
										}
										get_roster_player_note_endpoint={
											endpoints.get_roster_player_note_endpoint
										}
										create_roster_player_note_endpoint={
											endpoints.create_roster_player_note_endpoint
										}
										update_roster_player_note_endpoint={
											endpoints.update_roster_player_note_endpoint
										}
										get_notes_by_roster_endpoint={
											endpoints.get_notes_by_roster_endpoint
										}
										update_notes_by_roster_endpoint={
											endpoints.update_notes_by_roster_endpoint
										}
										get_draft_notes_by_roster_endpoint={
											endpoints.get_draft_notes_by_roster_endpoint
										}
										update_draft_notes_by_roster_endpoint={
											endpoints.update_draft_notes_by_roster_endpoint
										}
										get_lessons_learned_notes_by_roster_endpoint={
											endpoints.get_lessons_learned_notes_by_roster_endpoint
										}
										update_lessons_learned_notes_by_roster_endpoint={
											endpoints.update_lessons_learned_notes_by_roster_endpoint
										}
										create_checkout_session_endpoint={
											endpoints.create_checkout_session_endpoint
										}
										stripe_public_key={stripe_public_key}
										stripe_product_essential_id={stripe_product_essential_id}
										stripe_product_advanced_id={stripe_product_advanced_id}
										stripe_product_ultimate_id={stripe_product_ultimate_id}
									/>
								}
							/>
							<Route
								path="/players/:id"
								element={
									<PlayerProfilePage
										get_player_endpoint={endpoints.get_player_endpoint}
										get_players_endpoint={endpoints.get_players_endpoint}
										get_player_ai_insights_endpoint={
											endpoints.get_player_ai_insights_endpoint
										}
										create_checkout_session_endpoint={
											endpoints.create_checkout_session_endpoint
										}
										stripe_public_key={stripe_public_key}
										stripe_product_essential_id={stripe_product_essential_id}
										stripe_product_advanced_id={stripe_product_advanced_id}
										stripe_product_ultimate_id={stripe_product_ultimate_id}
									/>
								}
							/>
							<Route
								path="/teams"
								element={
									<TeamsIndexPage
										get_teams_search_by_coach_endpoint={
											endpoints.get_teams_search_by_coach_endpoint
										}
									/>
								}
							/>
							<Route
								path="/teams/:id"
								element={
									<TeamProfilePage
										get_team_endpoint={endpoints.get_team_endpoint}
										get_teams_endpoint={endpoints.get_teams_endpoint}
										get_team_ai_insights_endpoint={
											endpoints.get_team_ai_insights_endpoint
										}
										create_checkout_session_endpoint={
											endpoints.create_checkout_session_endpoint
										}
										stripe_public_key={stripe_public_key}
										stripe_product_essential_id={stripe_product_essential_id}
										stripe_product_advanced_id={stripe_product_advanced_id}
										stripe_product_ultimate_id={stripe_product_ultimate_id}
									/>
								}
							/>
							<Route
								path="/projections"
								element={
									<ProjectionsIndexPage
										get_projection_teams_endpoint={
											endpoints.get_projection_teams_endpoint
										}
										get_projection_players_endpoint={
											endpoints.get_projection_players_endpoint
										}
									/>
								}
							/>
							<Route
								path="/player-rankings"
								element={
									<PlayerRankingsPage
										get_player_rankings_endpoint={
											endpoints.get_player_rankings_endpoint
										}
										import_player_rankings_endpoint={
											endpoints.import_player_rankings_endpoint
										}
										reorder_player_rankings_endpoint={
											endpoints.reorder_player_rankings_endpoint
										}
										export_player_rankings_endpoint={
											endpoints.export_player_rankings_endpoint
										}
										get_championship_weeks_endpoint={
											endpoints.get_championship_weeks_endpoint
										}
										get_eliminator_rankings_favorabilities_endpoint={
											endpoints.get_eliminator_rankings_favorabilities_endpoint
										}
									/>
								}
							/>
							<Route
								path="/eliminator-player-matchups"
								element={
									<PrivateRoute>
										<EliminatorPlayerMatchupsPage
											get_eliminator_rankings_endpoint={
												endpoints.get_eliminator_rankings_endpoint
											}
											update_eliminator_rankings_endpoint={
												endpoints.update_eliminator_rankings_endpoint
											}
											import_eliminator_rankings_endpoint={
												endpoints.import_eliminator_rankings_endpoint
											}
											export_eliminator_rankings_endpoint={
												endpoints.export_eliminator_rankings_endpoint
											}
											export_eliminator_defense_grades_endpoint={
												endpoints.export_eliminator_defense_grades_endpoint
											}
											export_eliminator_defense_matrix_endpoint={
												endpoints.export_eliminator_defense_matrix_endpoint
											}
											apply_eliminator_defense_grades_endpoint={
												endpoints.apply_eliminator_defense_grades_endpoint
											}
											get_championship_weeks_endpoint={
												endpoints.get_championship_weeks_endpoint
											}
										/>
									</PrivateRoute>
								}
							/>
							<Route
								path="/projections/team/:id"
								element={
									<ProjectionTeamPage
										get_projection_team_endpoint={
											endpoints.get_projection_team_endpoint
										}
										update_projection_team_endpoint={
											endpoints.update_projection_team_endpoint
										}
									/>
								}
							/>
							<Route
								path="/prospects"
								element={
									<ProspectsIndexPage
										get_players_endpoint={endpoints.get_players_endpoint}
										get_prospects_endpoint={endpoints.get_prospects_endpoint}
									/>
								}
							/>
							<Route
								path="/prospects/:id"
								element={
									<ProspectProfilePage
										get_prospect_endpoint={endpoints.get_prospect_endpoint}
										get_prospects_endpoint={endpoints.get_prospects_endpoint}
										get_player_endpoint={endpoints.get_player_endpoint}
										get_players_endpoint={endpoints.get_players_endpoint}
										create_checkout_session_endpoint={
											endpoints.create_checkout_session_endpoint
										}
										stripe_public_key={stripe_public_key}
										stripe_product_essential_id={stripe_product_essential_id}
										stripe_product_advanced_id={stripe_product_advanced_id}
										stripe_product_ultimate_id={stripe_product_ultimate_id}
									/>
								}
							/>
							<Route
								path="/help-center"
								element={
									<HelpCenterIndexPage
										get_articles_endpoint={endpoints.get_articles_endpoint}
									/>
								}
							/>
							<Route
								path="/help-center/:id"
								element={
									<ArticlePage
										get_article_endpoint={endpoints.get_article_endpoint}
									/>
								}
							/>
							<Route
								path="/news"
								element={
									<NewsCenterIndexPage
										get_articles_endpoint={endpoints.get_articles_endpoint}
									/>
								}
							/>
							<Route path="/yahoo/callback" element={<YahooCallbackPage />} />
							<Route
								path="/glossary"
								element={
									<GlossaryIndexPage
										get_glossary_terms_endpoint={
											endpoints.get_glossary_terms_endpoint
										}
									/>
								}
							/>
							<Route path="/yahoo/callback" element={<YahooCallbackPage />} />

							{/* User Specific/Log In Required Routes */}
							<Route
								path="/dashboard"
								element={
									<PrivateRoute>
										<DashboardPage
											get_articles_endpoint={endpoints.get_articles_endpoint}
											get_player_endpoint={endpoints.get_player_endpoint}
											get_team_endpoint={endpoints.get_team_endpoint}
											get_prospect_endpoint={endpoints.get_prospect_endpoint}
											get_rosters_by_user_endpoint={
												endpoints.get_rosters_by_user_endpoint
											}
											get_courses_endpoint={endpoints.get_courses_endpoint}
											get_social_posts_endpoint={
												endpoints.get_social_posts_endpoint
											}
											create_checkout_session_endpoint={
												endpoints.create_checkout_session_endpoint
											}
											stripe_public_key={stripe_public_key}
											stripe_product_essential_id={stripe_product_essential_id}
											stripe_product_advanced_id={stripe_product_advanced_id}
											stripe_product_ultimate_id={stripe_product_ultimate_id}
										/>
									</PrivateRoute>
								}
							/>
							<Route
								path="/front-office"
								element={
									<PrivateRoute>
										<FrontOfficePage
											get_user_endpoint={endpoints.get_user_endpoint}
											update_user_endpoint={endpoints.update_user_endpoint}
											get_subscriptions_by_user_endpoint={
												endpoints.get_subscriptions_by_user_endpoint
											}
											create_checkout_session_endpoint={
												endpoints.create_checkout_session_endpoint
											}
											stripe_public_key={stripe_public_key}
											stripe_product_essential_id={stripe_product_essential_id}
											stripe_product_advanced_id={stripe_product_advanced_id}
											stripe_product_ultimate_id={stripe_product_ultimate_id}
											create_billing_portal_session_endpoint={
												endpoints.create_billing_portal_session_endpoint
											}
										/>
									</PrivateRoute>
								}
							/>
							<Route
								path="/best-ball-zone"
								element={
									<PrivateRoute>
										<BestBallZonePage />
									</PrivateRoute>
								}
							/>
							<Route
								path="/grade-my-draft"
								element={
									<PrivateRoute>
										<GradeMyDraftPage
											grade_best_ball_draft_endpoint={
												endpoints.grade_best_ball_draft_endpoint
											}
										/>
									</PrivateRoute>
								}
							/>
							<Route
								path="/draft-build-spectrum"
								element={
									<PrivateRoute>
										<DraftBuildSpectrumPage />
									</PrivateRoute>
								}
							/>
							<Route
								path="/spike-volatility-index"
								element={
									<PrivateRoute>
										<SpikeVolatilityIndexPage />
									</PrivateRoute>
								}
							/>
							<Route
								path="/championship-weeks"
								element={
									<PrivateRoute>
										<ChampionshipWeeksPage
											get_championship_weeks_endpoint={
												endpoints.get_championship_weeks_endpoint
											}
											get_championship_weeks_team_players_endpoint={
												endpoints.get_championship_weeks_team_players_endpoint
											}
										/>
									</PrivateRoute>
								}
							/>
							<Route
								path="/ai-trainer"
								element={
									<PrivateRoute>
										<AiTrainerPage
											get_user_fitness_profile_trainer_endpoint={
												endpoints.get_user_fitness_profile_trainer_endpoint
											}
											update_user_fitness_profile_trainer_settings_endpoint={
												endpoints.update_user_fitness_profile_trainer_settings_endpoint
											}
											chat_user_fitness_profile_trainer_endpoint={
												endpoints.chat_user_fitness_profile_trainer_endpoint
											}
											analyze_user_fitness_profile_trainer_meal_endpoint={
												endpoints.analyze_user_fitness_profile_trainer_meal_endpoint
											}
											clear_user_fitness_profile_trainer_chat_endpoint={
												endpoints.clear_user_fitness_profile_trainer_chat_endpoint
											}
											accept_user_fitness_profile_trainer_challenge_endpoint={
												endpoints.accept_user_fitness_profile_trainer_challenge_endpoint
											}
											dismiss_user_fitness_profile_trainer_briefing_endpoint={
												endpoints.dismiss_user_fitness_profile_trainer_briefing_endpoint
											}
										/>
									</PrivateRoute>
								}
							/>
							<Route
								path="/fitness-hub"
								element={
									<PrivateRoute>
										<FitnessHubPage />
									</PrivateRoute>
								}
							/>
							<Route
								path="/friends"
								element={
									<PrivateRoute>
										<FriendsPage />
									</PrivateRoute>
								}
							/>
							<Route
								path="/linked-households"
								element={
									<PrivateRoute>
										<LinkedHouseholdsPage />
									</PrivateRoute>
								}
							/>
							<Route
								path="/challenge-wire"
								element={
									<PrivateRoute>
										<ChallengeWirePage />
									</PrivateRoute>
								}
							/>
							<Route
								path="/community-challenges/:id"
								element={
									<PrivateRoute>
										<CommunityChallengeShowPage />
									</PrivateRoute>
								}
							/>
							<Route
								path="/fitness-profiles"
								element={
									<PrivateRoute>
										<FitnessProfilesIndexPage />
									</PrivateRoute>
								}
							/>
							<Route
								path="/fitness-profiles/:id/complete-intake"
								element={
									<PrivateRoute>
										<FitnessProfileCompleteIntakePage />
									</PrivateRoute>
								}
							/>
							<Route
								path="/fitness-profiles/:id"
								element={
									<PrivateRoute>
										<FitnessProfileDetailPage />
									</PrivateRoute>
								}
							/>
							<Route
								path="/fitness-profiles/new"
								element={
									<PrivateRoute>
										<FitnessProfileCreatePage />
									</PrivateRoute>
								}
							/>
							<Route
								path="/nutrition-lab"
								element={
									<PrivateRoute>
										<NutritionLabPage
											analyze_food_photos_endpoint={
												endpoints.analyze_food_photos_endpoint
											}
											ai_recipe_history_endpoint={
												endpoints.ai_recipe_history_endpoint
											}
											recipe_catalog_endpoint={
												endpoints.recipe_catalog_endpoint
											}
											recipe_catalog_suggest_endpoint={
												endpoints.recipe_catalog_suggest_endpoint
											}
										/>
									</PrivateRoute>
								}
							/>
							<Route
								path="/classroom"
								element={
									<PrivateRoute>
										<ClassroomPage
											get_players_endpoint={endpoints.get_players_endpoint}
											get_player_endpoint={endpoints.get_player_endpoint}
										/>
									</PrivateRoute>
								}
							/>
							<Route
								path="/whiteboard"
								element={
									<PrivateRoute>
										<WhiteboardPage />
									</PrivateRoute>
								}
							/>
							<Route
								path="/alerts"
								element={
									<PrivateRoute>
										<AlertsInboxPage />
									</PrivateRoute>
								}
							/>
							<Route
								path="/draft-tracker"
								element={
									<PrivateRoute>
										<DraftTrackerPage
											get_player_endpoint={endpoints.get_player_endpoint}
											get_players_endpoint={endpoints.get_players_endpoint}
											get_team_endpoint={endpoints.get_team_endpoint}
											get_roster_by_user_endpoint={
												endpoints.get_roster_by_user_endpoint
											}
											create_roster_by_user_endpoint={
												endpoints.create_roster_by_user_endpoint
											}
											update_roster_by_user_endpoint={
												endpoints.update_roster_by_user_endpoint
											}
											create_roster_player_by_roster_endpoint={
												endpoints.create_roster_player_by_roster_endpoint
											}
											delete_roster_player_by_roster_endpoint={
												endpoints.delete_roster_player_by_roster_endpoint
											}
											get_roster_player_note_endpoint={
												endpoints.get_roster_player_note_endpoint
											}
											create_roster_player_note_endpoint={
												endpoints.create_roster_player_note_endpoint
											}
											update_roster_player_note_endpoint={
												endpoints.update_roster_player_note_endpoint
											}
											get_notes_by_roster_endpoint={
												endpoints.get_notes_by_roster_endpoint
											}
											update_notes_by_roster_endpoint={
												endpoints.update_notes_by_roster_endpoint
											}
											get_draft_notes_by_roster_endpoint={
												endpoints.get_draft_notes_by_roster_endpoint
											}
											update_draft_notes_by_roster_endpoint={
												endpoints.update_draft_notes_by_roster_endpoint
											}
											get_lessons_learned_notes_by_roster_endpoint={
												endpoints.get_lessons_learned_notes_by_roster_endpoint
											}
											update_lessons_learned_notes_by_roster_endpoint={
												endpoints.update_lessons_learned_notes_by_roster_endpoint
											}
											create_checkout_session_endpoint={
												endpoints.create_checkout_session_endpoint
											}
											stripe_public_key={stripe_public_key}
											stripe_product_essential_id={stripe_product_essential_id}
											stripe_product_advanced_id={stripe_product_advanced_id}
											stripe_product_ultimate_id={stripe_product_ultimate_id}
										/>
									</PrivateRoute>
								}
							/>
							<Route
								path="/rosters"
								element={
									<PrivateRoute>
										<RostersIndexPage
											get_rosters_by_user_endpoint={
												endpoints.get_rosters_by_user_endpoint
											}
											get_roster_by_user_endpoint={
												endpoints.get_roster_by_user_endpoint
											}
											create_roster_by_user_endpoint={
												endpoints.create_roster_by_user_endpoint
											}
											update_roster_by_user_endpoint={
												endpoints.update_roster_by_user_endpoint
											}
											delete_roster_by_user_endpoint={
												endpoints.delete_roster_by_user_endpoint
											}
											get_rosters_players_exposures_by_user_endpoint={
												endpoints.get_rosters_players_exposures_by_user_endpoint
											}
											get_rosters_players_injuries_by_user_endpoint={
												endpoints.get_rosters_players_injuries_by_user_endpoint
											}
											create_checkout_session_endpoint={
												endpoints.create_checkout_session_endpoint
											}
											stripe_public_key={stripe_public_key}
											stripe_product_essential_id={stripe_product_essential_id}
											stripe_product_advanced_id={stripe_product_advanced_id}
											stripe_product_ultimate_id={stripe_product_ultimate_id}
										/>
									</PrivateRoute>
								}
							/>
							<Route
								path="/rosters/:id"
								element={
									<PrivateRoute>
										<RosterProfilePage
											get_roster_by_user_endpoint={
												endpoints.get_roster_by_user_endpoint
											}
											get_player_endpoint={endpoints.get_player_endpoint}
											get_players_endpoint={endpoints.get_players_endpoint}
											get_team_endpoint={endpoints.get_team_endpoint}
											update_roster_by_user_endpoint={
												endpoints.update_roster_by_user_endpoint
											}
											create_roster_player_by_roster_endpoint={
												endpoints.create_roster_player_by_roster_endpoint
											}
											delete_roster_player_by_roster_endpoint={
												endpoints.delete_roster_player_by_roster_endpoint
											}
											get_roster_player_note_endpoint={
												endpoints.get_roster_player_note_endpoint
											}
											create_roster_player_note_endpoint={
												endpoints.create_roster_player_note_endpoint
											}
											update_roster_player_note_endpoint={
												endpoints.update_roster_player_note_endpoint
											}
											get_notes_by_roster_endpoint={
												endpoints.get_notes_by_roster_endpoint
											}
											update_notes_by_roster_endpoint={
												endpoints.update_notes_by_roster_endpoint
											}
											get_draft_notes_by_roster_endpoint={
												endpoints.get_draft_notes_by_roster_endpoint
											}
											update_draft_notes_by_roster_endpoint={
												endpoints.update_draft_notes_by_roster_endpoint
											}
											get_lessons_learned_notes_by_roster_endpoint={
												endpoints.get_lessons_learned_notes_by_roster_endpoint
											}
											update_lessons_learned_notes_by_roster_endpoint={
												endpoints.update_lessons_learned_notes_by_roster_endpoint
											}
											get_prospects_endpoint={endpoints.get_prospects_endpoint}
											scrape_and_update_ffpc_roster_endpoint={
												endpoints.scrape_and_update_ffpc_roster_endpoint
											}
											get_roster_ai_insights_endpoint={
												endpoints.get_roster_ai_insights_endpoint
											}
											get_roster_ai_trade_ideas_endpoint={
												endpoints.get_roster_ai_trade_ideas_endpoint
											}
											get_player_ai_insights_endpoint={
												endpoints.get_player_ai_insights_endpoint
											}
											get_team_ai_insights_endpoint={
												endpoints.get_team_ai_insights_endpoint
											}
											get_league_rosters_endpoint={
												endpoints.get_league_rosters_endpoint
											}
											get_roster_schedule_info_endpoint={
												endpoints.get_roster_schedule_info_endpoint
											}
											get_roster_schedule_data_by_user_endpoint={
												endpoints.get_roster_schedule_data_by_user_endpoint
											}
											scrape_and_update_ffpc_league_rosters_endpoint={
												endpoints.scrape_and_update_ffpc_league_rosters_endpoint
											}
											scrape_and_update_ffpc_schedule_endpoint={
												endpoints.scrape_and_update_ffpc_schedule_endpoint
											}
											fetch_and_update_sleeper_league_rosters_endpoint={
												endpoints.fetch_and_update_sleeper_league_rosters_endpoint
											}
											fetch_and_update_sleeper_schedule_endpoint={
												endpoints.fetch_and_update_sleeper_schedule_endpoint
											}
											fetch_and_update_yahoo_league_rosters_endpoint={
												endpoints.fetch_and_update_yahoo_league_rosters_endpoint
											}
											fetch_and_update_yahoo_schedule_endpoint={
												endpoints.fetch_and_update_yahoo_schedule_endpoint
											}
											get_league_standings_endpoint={
												endpoints.get_league_standings_endpoint
											}
										/>
									</PrivateRoute>
								}
							/>
							<Route
								path="/courses"
								element={
									<PrivateRoute>
										<CoursesIndexPage
											get_course_endpoint={endpoints.get_course_endpoint}
											get_courses_endpoint={endpoints.get_courses_endpoint}
										/>
									</PrivateRoute>
								}
							/>
							<Route
								path="/courses/:id"
								element={
									<PrivateRoute>
										<CourseProfilePage
											get_course_endpoint={endpoints.get_course_endpoint}
										/>
									</PrivateRoute>
								}
							/>
							<Route
								path="/user-profile"
								element={
									<PrivateRoute>
										<UserProfilePage
											get_user_endpoint={endpoints.get_user_endpoint}
											update_user_endpoint={endpoints.update_user_endpoint}
											get_subscriptions_by_user_endpoint={
												endpoints.get_subscriptions_by_user_endpoint
											}
											create_checkout_session_endpoint={
												endpoints.create_checkout_session_endpoint
											}
											stripe_public_key={stripe_public_key}
											stripe_product_essential_id={stripe_product_essential_id}
											stripe_product_advanced_id={stripe_product_advanced_id}
											stripe_product_ultimate_id={stripe_product_ultimate_id}
											create_billing_portal_session_endpoint={
												endpoints.create_billing_portal_session_endpoint
											}
										/>
									</PrivateRoute>
								}
							/>
							{/* Redirect to 404 Not Found Page if no route matches */}
							<Route path="*" element={<h1>404 Not Found</h1>} />
						</Routes>
						</PageContent>
						<Footer create_issue_endpoint={endpoints.create_issue_endpoint} />
					</NavigationProvider>
				</ThemeProvider>
			</AuthProvider>
		</Router>
	);
}
